<template>
  <section class="coffee-preparation-section">
    <h5 class="single-page-signup-title">
      How would you like your coffee prepared?
    </h5>
    <div class="row mx-0">
      <div
        v-for="(bean_type, index) in bean_types"
        :key="index"
        :class="{
          'col-6 mb-3 t-center hand-pointer': true,
          'pl-0 pr-2': index === 0,
          'pl-2 pr-0': index === 1,
        }"
      >
        <div
          :class="{
            'single-page-signup-option': true,
            picked: userData.bean_type === bean_type.type
          }"
          @click="selectType(bean_type)"
        >
          <p class="single-page-signup-option-text mb-0">
            {{ bean_type.display }}
          </p>
        </div>
      </div>
      <div class="col-12 px-0">
        <div
          :class="{
            'single-page-signup-option hand-pointer': true,
            picked: userData.brewing_method === picked_brewing_method
          }"
          @click="openBrewingMethodsModal()"
        >
          <div class="row mx-0">
            <div class="col-10 pl-0">
              <p v-if="picked_brewing_method" class="single-page-signup-option-text mb-0">
                {{ picked_brewing_method.toLowerCase().includes('stove') ? 'Moka Pot' : picked_brewing_method }}
              </p>
            </div>
            <div class="col-2 px-0 d-flex align-items-center justify-content-end">
              <img
                class="single-page-signup-option-arrow"
                src="/assets/icons/secondary/new_checkout_arrow_down.svg"
                alt="open preparation options"
              >
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
  <DesktopOverlay
    :is_active="is_desktop_overlay_active"
    class_modifier="small-padding darker"
    @overlay-click="flipDualOverlay('desktop')"
  >
    <template #overlayContent>
      <img
        class="single-page-signup-modal-btn-close header-icons right hand-pointer"
        src="/assets/icons/fillable/close-2.svg"
        alt="close step info modal"
        @click="flipDualOverlay('desktop')"
      >
      <div class="single-page-signup-modal mx-auto">
        <div class="col-12 px-0 row mx-0">
          <div
            v-for="prep in coffee_preparations"
            :key="prep.value"
            class="col-4 row mx-0 mb-2 t-center hand-pointer mb-lg-4"
            @click="selectBrewingMethod(prep.display, prep.coffee_type === 'espresso' ? 'espresso' : 'brewed', prep.grind_size, prep.value, prep.valueBE, 'desktop' )"
          >
            <div
              :class="{
                'col-12 px-0 single-page-signup-modal-option hand-pointer': true,
                picked: userData.brewing_method === prep.display
              }"
            >
              <img
                v-if="prep.value === 'stove top'"
                class="prep-img stovetop"
                :src="prep.image"
                :alt="prep.display"
              >
              <img
                v-else
                :class="{ 'prep-img': true, [prep.value]: true }"
                :src="prep.image"
                :alt="prep.display"
              >
            </div>
            <div class="col-12 px-0 single-page-signup-modal-text hand-pointer">
              {{ prep.value.toLowerCase().includes('stove') ? 'Moka Pot' : prep.display }}
            </div>
          </div>
        </div>
      </div>
    </template>
  </DesktopOverlay>
  <MobileOverlayFull
    :is_active="is_mobile_overlay_active"
    customization="darker padding-mob"
    @overlay-click="flipDualOverlay('mobile')"
  >
    <template #overlayContent>
      <img
        class="single-page-signup-modal-btn-close header-icons right hand-pointer"
        src="/assets/icons/fillable/close-2.svg"
        alt="close step info modal"
        @click="flipDualOverlay('mobile')"
      >
      <div class="single-page-signup-modal mx-auto">
        <div class="col-12 px-0 row mx-0">
          <div
            v-for="prep in coffee_preparations"
            :key="prep.value"
            class="col-4 row mx-0 mb-2 t-center hand-pointer mb-lg-4"
            @click="selectBrewingMethod(prep.display, prep.coffee_type === 'espresso' ? 'espresso' : 'brewed', prep.grind_size, prep.value, prep.valueBE, 'mobile')"
          >
            <div
              :class="{
                'col-12 px-0 single-page-signup-modal-option hand-pointer': true,
                picked: userData.brewing_method === prep.display
              }"
            >
              <img
                v-if="prep.value === 'stove top'"
                class="prep-img stovetop"
                :src="prep.image"
                :alt="prep.display"
              >
              <img
                v-else
                :class="{ 'prep-img': true, [prep.value]: true }"
                :src="prep.image"
                :alt="prep.display"
              >
            </div>
            <div class="col-12 px-0 single-page-signup-modal-text hand-pointer">
              {{ prep.value.toLowerCase().includes('stove') ? 'Moka Pot' : prep.display }}
            </div>
          </div>
        </div>
      </div>
    </template>
  </MobileOverlayFull>
</template>

<script setup>
import * as GTM from '../../../utils/analytics/gtm'
import { useStore } from 'vuex'
import { inject, ref, onMounted, defineAsyncComponent } from 'vue'
import { useDualOverlayHelpers } from '../../utils/Overlays/dualOverlayHelpers'
import { useMobileHelpers } from '../../utils/composables/mobileHelpers'
import { usePreparations } from '../../SignupA/composables/preparations'

const DesktopOverlay = defineAsyncComponent(() => import('../../utils/Overlays/DesktopOverlay.vue'))
const MobileOverlayFull = defineAsyncComponent(() => import('../../utils/Overlays/MobileOverlayFull.vue'))


const store = useStore()
const userData = inject('userData')
const voucherData = inject('voucherData')
const updateUserData = inject('updateUserData')
const setPlanCache = inject('setPlanCache')
// Reactive variables (inc composables)
const { is_desktop_overlay_active, is_mobile_overlay_active, flipDualOverlay } = useDualOverlayHelpers()
const { isTablet, isMobile } = useMobileHelpers()
const bean_types = ref([
  {
    display: 'Wholebean',
    type: 'wholebean'
  },
  {
    display: 'Ground',
    type: 'ground'
  }
])
const picked_bean_type = ref(undefined)
const bag_types = ref(['regular', 'decaf'])
const is_wholebean = ref(undefined)
const { coffee_preparations } = usePreparations()
const picked_brewing_method = ref(undefined)
const previous_coffee_type = ref('')
const previous_method = ref('')
// Computed
// Methods
function selectType(bean_type) {
	picked_bean_type.value = bean_type.type
	is_wholebean.value = picked_bean_type.value === 'wholebean' ? true : false

  updateUserData({
    bean_type: picked_bean_type.value,
    is_wholebean: is_wholebean.value,
    grind_size: is_wholebean.value ? 'wholebean' : userData.value.recommended_grind !== undefined ? userData.value.recommended_grind : undefined ,
    recommended_grind: userData.value.recommended_grind,
  })

  if (userData.value?.bean_type !== undefined) {
		const plan_data = {
			type: picked_bean_type.value,
			coffee_type: picked_bean_type.value,
			grind: is_wholebean.value ? 'wholebean' : undefined,
			method: undefined,
			range: undefined,
			range_sku: undefined,
			coffee: undefined,
			coffee_sku: undefined,
			amount: undefined,
			coffee_plan_sku: undefined,
		}
		GTM.funnelProgress(3, 'pick_coffee_type', plan_data, 'bag')

		var objectInfo = {}
    if (bag_types.value.includes(userData.value?.type_name)){
      var grind_size = userData.value?.grind_size !== undefined && userData.value?.grind_size !== '' ?
        userData.value?.grind_size : userData.value?.recommended_grind
      var brew_method_be = userData.value?.brewing_method_be === 'wholebean' && !is_wholebean.value ?
        userData.value?.recommended_brewing_method_be : userData.value?.brewing_method_be
			objectInfo = {
				brewing_method: userData.value?.brewing_method,
				brewing_method_be: is_wholebean.value ? 'wholebean' : brew_method_be,
        grind_size: is_wholebean.value
          ? 'wholebean'
          : grind_size,
				coffee_type: userData.value?.coffee_type,
			}
    } else {
			objectInfo = {
				brewing_method: undefined,
				brewing_method_be: undefined,
				grind_size: is_wholebean.value
          ? 'wholebean'
          : undefined,
				coffee_type: undefined,
			}
    }
    updateUserData({
			...objectInfo,
			bean_type:
        is_wholebean.value
          ? 'wholebean'
          : 'ground',
			is_wholebean: is_wholebean.value,
		})
    setPlanCache(1, false)
  }
}

function coffee_type_changed(){
  return previous_coffee_type.value !== '' && previous_coffee_type.value !== userData.value.coffee_type
}

function get_preparation(method) {
	return coffee_preparations.value.find((prep) => { return prep.display === method })
}

function openBrewingMethodsModal(){
  flipDualOverlay(isMobile.value && !isTablet.value ? 'mobile' : 'desktop')
}

function selectBrewingMethod(opt_display, coffee_type, grind, opt_value, opt_valueBE, id) {
	picked_brewing_method.value = opt_display
	updateUserData({
		brewing_method: opt_display,
    brewing_method_be: is_wholebean.value ? 'wholebean' : opt_valueBE,
		coffee_type: coffee_type,
    recommended_grind: grind,
    recommended_brewing_method_be: opt_valueBE
	})

  if (userData.value?.brewing_method !== undefined) {
    var methodBE = {'Espresso Machine': 'Espresso', 'Stovetop': 'Stove Top'}
    var brewing_method = userData.value.brewing_method === 'Espresso Machine' || userData.value.brewing_method === 'Stovetop'
      ? methodBE[userData.value.brewing_method] : userData.value.brewing_method
		var method = get_preparation(brewing_method)
		updateUserData({
      brewing_method_be: is_wholebean.value ? 'wholebean' : method.valueBE,
      coffee_type: method.coffee_type,
			grind_size: userData.value?.grind_size !== undefined && userData.value?.grind_size !== '' && userData.value?.is_wholebean
				? userData.value?.grind_size
				: userData.value?.recommended_grind
		})

		GTM.funnelProgress(4, 'pick_method', { method: userData.value.brewing_method }, 'bag')
    flipDualOverlay(id)
		store.dispatch('shop/getFunnelCoffeesRange', {
      coffee_type: userData.value.decaf ? 'brewed' : method.coffee_type, decaf: userData.value.decaf, voucher_code: voucherData.value.voucher_code })
    // if coffee type changed force step update
		setPlanCache(2, coffee_type_changed() === true && !userData.value.decaf ? true : false)
  }
}
onMounted(() => {
	// check if it's back
	is_wholebean.value = userData.value?.is_wholebean
	picked_bean_type.value = userData.value?.bean_type
  if (
		userData.value?.grind_size &&
		userData.value?.grind_size !== '' &&
		userData.value?.brewing_method &&
		userData.value?.brewing_method !== ''
	) {
    var methodBE = {'Espresso Machine': 'Espresso', 'Stovetop': 'Stove Top'}
    var brewing_method = userData.value.brewing_method === 'Espresso Machine' || userData.value.brewing_method === 'Stovetop'
      ? methodBE[userData.value.brewing_method] : userData.value.brewing_method
		var method = get_preparation(brewing_method)
		picked_brewing_method.value = method.display
    previous_coffee_type.value = method.coffee_type
    previous_method.value = userData.value.brewing_method

    store.dispatch('shop/getFunnelCoffeesRange', {
      coffee_type: userData.value.decaf ? 'brewed' : method.coffee_type, decaf: userData.value.decaf, voucher_code: voucherData.value.voucher_code })
  }
})
</script>

<script>
export default {
  name: 'CoffeePreparation',
}
</script>

<style lang="scss" scoped>
.coffee-preparation-section {
  padding-top: 30px;
  padding-bottom: 30px;
}
</style>